/* ==========================================================================
   PW — Motion engine
   --------------------------------------------------------------------------
   Vanilla, no dependencies, no globals beyond window.PWMotion.
   Idempotent + re-initialisable so it survives Shopify theme-editor reloads.

   Mechanics are ported from the source design system, not approximated:
     - reveal      : IO threshold 0, once. translateY(20px)->0 + opacity,
                     750ms cubic-bezier(.5,0,0,1), 250ms * index stagger.
                     Under reduced motion the element is simply shown.
     - progress    : writes --pw-progress as a UNITLESS 0..100 number.
                     Hero mode saturates over exactly one viewport height.
     - carousel    : the CSS bar-progress animation IS the clock; we advance on
                     `animationend`. No setInterval, so nothing can drift.
                     Restarting requires a forced reflow.
     - accordion   : grid-template-rows 0fr->1fr; the panel child must carry
                     min-height:0 (handled in CSS).
     - inview theme: each module writes its theme onto <body> via a 10%-tall
                     detection band, which is what lets fixed chrome invert.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');

  function reduced() { return REDUCED.matches; }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  function claim(el, key) {
    var k = 'pwInit' + key;
    if (el.dataset[k]) return false;
    el.dataset[k] = '1';
    return true;
  }

  /* ---------------------------------------------------------------- reveal */
  var revealObserver = null;

  function initReveal(root) {
    var els = $$('[data-pw-reveal]', root).filter(function (el) { return claim(el, 'Reveal'); });
    if (!els.length) return;

    /* Reduced motion: no animation at all — just show it. */
    if (reduced()) {
      els.forEach(function (el) {
        el.setAttribute('data-pw-revealed', '');
        el.setAttribute('data-pw-settled', '');
      });
      return;
    }

    if (!revealObserver) {
      /* threshold 0 == fires the instant 1px crosses the viewport edge. */
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = 0;

          var group = el.closest('[data-pw-reveal-group]');
          if (group) {
            var step = parseFloat(group.getAttribute('data-pw-reveal-group'));
            if (isNaN(step)) step = 250;
            var sibs = $$('[data-pw-reveal]', group);
            var idx = sibs.indexOf(el);
            if (idx > -1) delay = idx * step;
          }
          var own = el.getAttribute('data-pw-reveal-delay');
          if (own !== null && own !== '') delay = parseFloat(own) || 0;

          el.style.setProperty('--pw-reveal-delay', delay + 'ms');
          el.setAttribute('data-pw-revealed', '');
          revealObserver.unobserve(el);

          /* Release the compositor layer once the transition has finished. */
          window.setTimeout(function () {
            el.setAttribute('data-pw-settled', '');
          }, delay + 900);
        });
      }, { threshold: 0 });
    }

    els.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ------------------------------------------------------- scroll progress */
  /* --pw-progress is a UNITLESS number 0..100. Writing "50%" would collapse
     every calc() that consumes it. */
  var progressEls = [];

  function initProgress(root) {
    $$('[data-pw-progress]', root).forEach(function (el) {
      if (!claim(el, 'Progress')) return;
      progressEls.push(el);
    });
    if (progressEls.length) scheduleProgress();
  }

  function measureProgress() {
    var vh = window.innerHeight || document.documentElement.clientHeight;

    for (var i = progressEls.length - 1; i >= 0; i--) {
      var el = progressEls[i];
      if (!el.isConnected) { progressEls.splice(i, 1); continue; }

      var mode = el.getAttribute('data-pw-progress') || 'element';
      var p;

      if (mode === 'hero') {
        /* Saturates over exactly one viewport height, measured from page top.
           Only correct when the element is the first thing on the page. */
        p = window.scrollY / vh * 100;
      } else if (mode === 'pin') {
        var rp = el.getBoundingClientRect();
        p = (-rp.top) / Math.max(1, rp.height - vh) * 100;
      } else {
        /* Element-relative: 0 when the top edge reaches the viewport bottom
           minus startOffset, 100 once it has travelled its own height. */
        var r = el.getBoundingClientRect();
        var startOffset = vh / 2;
        p = Math.max(vh - r.top - startOffset, 0) / Math.max(1, el.offsetHeight) * 100;
      }

      p = clamp(p, 0, 100);
      el.style.setProperty('--pw-progress', p.toFixed(2));
      el.toggleAttribute('data-pw-scrolling', p > 3.5 && p < 100);
    }
  }

  var progressTicking = false;
  function scheduleProgress() {
    if (progressTicking) return;
    progressTicking = true;
    requestAnimationFrame(function () {
      measureProgress();
      progressTicking = false;
    });
  }

  /* ------------------------------------------------- body[data-pw-theme] */
  /* A 10%-tall detection band near the bottom of the viewport; whichever
     module sits in it writes its theme onto <body>, so fixed chrome (sticky
     CTA) can invert against the section it overlaps. */
  function initInviewTheme(root) {
    var els = $$('[data-pw-theme]', root).filter(function (el) { return claim(el, 'Theme'); });
    if (!els.length) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var t = entry.target.getAttribute('data-pw-theme');
        if (t) document.body.setAttribute('data-pw-inview-theme', t);
      });
    }, { rootMargin: '-80% 0% -10% 0%' });

    els.forEach(function (el) { obs.observe(el); });
  }

  /* ------------------------------------------------------------ count-up */
  function initCount(root) {
    var els = $$('[data-pw-count]', root).filter(function (el) { return claim(el, 'Count'); });
    if (!els.length) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        obs.unobserve(el);

        var target = parseFloat(el.getAttribute('data-pw-count'));
        if (isNaN(target)) return;
        var decimals = parseInt(el.getAttribute('data-pw-count-decimals') || '0', 10);
        var dur = parseInt(el.getAttribute('data-pw-count-duration') || '1600', 10);
        var locale = el.getAttribute('data-pw-count-locale') || 'de-DE';
        function fmt(v) {
          return v.toLocaleString(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
          });
        }

        if (reduced()) { el.textContent = fmt(target); return; }

        var start = null;
        function step(ts) {
          if (start === null) start = ts;
          var t = clamp((ts - start) / dur, 0, 1);
          var eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
          el.textContent = fmt(target * eased);
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = fmt(target);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.4 });

    els.forEach(function (el) { obs.observe(el); });
  }

  /* ------------------------------------------------------------- marquee */
  function initMarquee(root) {
    $$('[data-pw-marquee]', root).forEach(function (el) {
      if (!claim(el, 'Marquee')) return;
      var track = el.querySelector('.pw-marquee__track');
      if (!track) return;

      var original = track.innerHTML;
      var guard = 0;
      while (track.scrollWidth < el.offsetWidth * 2 && guard < 12) {
        track.innerHTML += original;
        guard++;
      }
      var speed = parseFloat(el.getAttribute('data-pw-marquee')) || 40; /* px/s */
      var dist = track.scrollWidth / 2;
      if (dist > 0) track.style.setProperty('--pw-marquee-duration', (dist / speed) + 's');
      el.setAttribute('data-pw-marquee-ready', '');
    });
  }

  /* ------------------------------------------------------------ carousel */
  /* The CSS `pw-bar-progress` animation is the clock. We advance on
     `animationend`. Restarting the bar requires a forced reflow — simply
     re-assigning the same animation name does not re-trigger it. */
  function initCarousel(root) {
    $$('[data-pw-carousel]', root).forEach(function (el) {
      if (!claim(el, 'Carousel')) return;

      var items = $$('[data-pw-carousel-item]', el);
      var panels = $$('[data-pw-carousel-panel]', el);
      if (!items.length) return;

      var index = 0;
      var paused = true; /* stays paused until scrolled into view */

      function railOf(i) {
        return items[i] ? items[i].querySelector('[data-pw-carousel-rail]') : null;
      }

      function syncMedia() {
        panels.forEach(function (p, n) {
          var v = p.querySelector('video');
          if (!v) return;
          if (n === index && !paused && !reduced()) {
            var play = v.play();
            if (play && play.catch) play.catch(function () {});
          } else {
            v.pause();
          }
        });
      }

      function show(i) {
        index = (i + items.length) % items.length;
        items.forEach(function (it, n) {
          it.toggleAttribute('data-pw-active', n === index);
          var btn = it.querySelector('[data-pw-carousel-button]');
          if (btn) btn.setAttribute('aria-expanded', n === index ? 'true' : 'false');
        });
        panels.forEach(function (p, n) { p.toggleAttribute('data-pw-active', n === index); });
        restartRail();
        syncMedia();
      }

      function restartRail() {
        var rail = railOf(index);
        if (!rail) return;
        rail.style.animation = 'none';
        void rail.offsetWidth;      /* forced reflow — required */
        rail.style.animation = '';
        rail.style.animationPlayState = paused || reduced() ? 'paused' : 'running';
      }

      function setPaused(v) {
        paused = v;
        var rail = railOf(index);
        if (rail) rail.style.animationPlayState = (v || reduced()) ? 'paused' : 'running';
        syncMedia();
      }

      /* Advance when the active rail finishes filling. */
      el.addEventListener('animationend', function (e) {
        if (!e.target.hasAttribute || !e.target.hasAttribute('data-pw-carousel-rail')) return;
        if (reduced() || paused) return;
        show(index + 1);
      });

      items.forEach(function (it, n) {
        var btn = it.querySelector('[data-pw-carousel-button]') || it;
        btn.addEventListener('click', function () { show(n); });
      });

      el.addEventListener('mouseenter', function () { setPaused(true); });
      el.addEventListener('mouseleave', function () { setPaused(false); });

      var vis = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { setPaused(!e.isIntersecting); });
      }, { threshold: 0.7 });
      vis.observe(el);

      show(0);
    });
  }

  /* ----------------------------------------------------------- accordion */
  function initAccordion(root) {
    $$('[data-pw-accordion]', root).forEach(function (el) {
      if (!claim(el, 'Accordion')) return;
      var single = el.getAttribute('data-pw-accordion') === 'single';

      $$('[data-pw-accordion-item]', el).forEach(function (item) {
        var trigger = item.querySelector('[data-pw-accordion-trigger]');
        var panel = item.querySelector('[data-pw-accordion-panel]');
        if (!trigger || !panel) return;

        var open0 = item.hasAttribute('data-pw-open');
        trigger.setAttribute('aria-expanded', open0 ? 'true' : 'false');
        panel.setAttribute('aria-hidden', open0 ? 'false' : 'true');

        trigger.addEventListener('click', function () {
          var open = item.hasAttribute('data-pw-open');

          if (single && !open) {
            $$('[data-pw-accordion-item][data-pw-open]', el).forEach(function (other) {
              other.removeAttribute('data-pw-open');
              var t = other.querySelector('[data-pw-accordion-trigger]');
              var p = other.querySelector('[data-pw-accordion-panel]');
              if (t) t.setAttribute('aria-expanded', 'false');
              if (p) p.setAttribute('aria-hidden', 'true');
            });
          }

          if (open) item.removeAttribute('data-pw-open');
          else item.setAttribute('data-pw-open', '');

          trigger.setAttribute('aria-expanded', !open ? 'true' : 'false');
          panel.setAttribute('aria-hidden', !open ? 'false' : 'true');
        });
      });
    });
  }

  /* ---------------------------------------------------------- sticky CTA */
  function initStickyCta(root) {
    $$('[data-pw-sticky-cta]', root).forEach(function (el) {
      if (!claim(el, 'StickyCta')) return;

      var heroSel = el.getAttribute('data-pw-sticky-cta');
      var hero = heroSel ? document.querySelector(heroSel) : null;
      var footer = document.querySelector('#footer');
      var shown = false;

      /* On a product page the bar's link points at the page we are already on,
         so tapping it just reloaded the page and added nothing. When a PW buy
         box is present, the bar submits that form instead, carrying whatever
         style/size the shopper picked through pw-pdp.js's add-to-cart flow. */
      var buyForm = document.querySelector('form[data-pw-form]');
      var link = el.querySelector('a');
      if (buyForm && link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          var add = buyForm.querySelector('[data-pw-add]');
          if (add && add.disabled) {
            buyForm.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
          } else if (typeof buyForm.requestSubmit === 'function') {
            buyForm.requestSubmit(add || undefined);
          } else if (add) {
            add.click();
          }
        });
      }

      function visible(node, ratio) {
        if (!node) return false;
        var r = node.getBoundingClientRect();
        var vh = window.innerHeight || 0;
        var seen = Math.min(r.bottom, vh) - Math.max(r.top, 0);
        return seen > 0 && seen >= r.height * ratio;
      }

      function update() {
        /* Hidden while the hero is ≥80% visible, or once the footer is in view. */
        var hide = visible(hero, 0.8) || visible(footer, 0.001);
        if (!hero) hide = window.scrollY < window.innerHeight * 0.6 || visible(footer, 0.001);
        /* Never float over the buy box itself: on phones it is taller than the
           screen, so the 80% hero rule kept the bar parked on the size picker. */
        if (buyForm && visible(buyForm, 0.001)) hide = true;

        if (hide === !shown) return;
        shown = !hide;
        el.toggleAttribute('data-pw-visible', shown);
      }

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ------------------------------------------------------------- header */
  function initHeader(root) {
    $$('[data-pw-header]', root).forEach(function (el) {
      if (!claim(el, 'Header')) return;
      var last = window.scrollY;

      function update() {
        var y = window.scrollY;
        el.toggleAttribute('data-pw-scrolled', y > 12);
        if (y <= 100) { el.removeAttribute('data-pw-hidden'); last = y; return; }
        if (y > last) el.setAttribute('data-pw-hidden', '');
        else el.removeAttribute('data-pw-hidden');
        last = y;
      }
      /* Deferred to avoid a first-paint flash, as in the source. */
      window.setTimeout(function () {
        window.addEventListener('scroll', update, { passive: true });
        update();
      }, 100);
    });
  }

  /* ---------------------------------------------- sticky scroll-scene stack */
  function initScenes(root) {
    $$('[data-pw-scenes]', root).forEach(function (el) {
      if (!claim(el, 'Scenes')) return;
      var scenes = $$('[data-pw-scene]', el);
      if (!scenes.length) return;

      function update() {
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        var total = Math.max(1, el.offsetHeight - vh);
        var p = clamp((-r.top) / total, 0, 1);
        var i = Math.min(scenes.length - 1, Math.floor(p * scenes.length));
        scenes.forEach(function (s, n) { s.toggleAttribute('data-pw-active', n === i); });
      }
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ------------------------------------------------ magnetic + tilt (fine) */
  function initPointerFx(root) {
    if (!FINE.matches || reduced()) return;

    $$('[data-pw-magnetic]', root).forEach(function (el) {
      if (!claim(el, 'Magnetic')) return;
      var strength = parseFloat(el.getAttribute('data-pw-magnetic')) || 0.25;

      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--pw-mx', ((e.clientX - r.left - r.width / 2) * strength).toFixed(2) + 'px');
        el.style.setProperty('--pw-my', ((e.clientY - r.top - r.height / 2) * strength).toFixed(2) + 'px');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--pw-mx', '0px');
        el.style.setProperty('--pw-my', '0px');
      });
    });

    $$('[data-pw-tilt]', root).forEach(function (el) {
      if (!claim(el, 'Tilt')) return;
      var max = parseFloat(el.getAttribute('data-pw-tilt')) || 6;

      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--pw-rx', (-py * max).toFixed(2) + 'deg');
        el.style.setProperty('--pw-ry', (px * max).toFixed(2) + 'deg');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--pw-rx', '0deg');
        el.style.setProperty('--pw-ry', '0deg');
      });
    });
  }

  /* --------------------------------------------- theme header overlay tag */
  /* The conrise #header is already position:absolute over the content, so on
     pages with a PW hero we only need to tag it — pw-theme.css does the rest
     (transparent over the hero, solid + blurred once scrolled). */
  function tagThemeHeader() {
    /* Any page that OPENS with a dark PW module wants the transparent header —
       that is the hero on the homepage and the buy box on a product page. */
    if (!document.querySelector('.pw-hero, .pw-buy')) return;
    var h = document.getElementById('header');
    if (!h || !claim(h, 'ThemeHeader')) return;
    h.setAttribute('data-pw-overlay', '');
    h.setAttribute('data-pw-header', '');
  }

  /* ---------------------------------------------------------------- boot */
  function init(root) {
    root = root || document;
    tagThemeHeader();
    initReveal(root);
    initProgress(root);
    initInviewTheme(root);
    initCount(root);
    initMarquee(root);
    initCarousel(root);
    initAccordion(root);
    initStickyCta(root);
    initHeader(document);
    initScenes(root);
    initPointerFx(root);
  }

  window.addEventListener('scroll', scheduleProgress, { passive: true });
  window.addEventListener('resize', scheduleProgress, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });

  if (REDUCED.addEventListener) {
    REDUCED.addEventListener('change', function () { init(document); });
  }

  window.PWMotion = { init: init, refresh: scheduleProgress };
})();
